/**
 * React hook for WASM deck analysis
 */

import { useState, useEffect, useCallback } from 'react';
import { analyzeDeck } from '../lib/wasm-loader';
import type { DeckAnalysisRequest, CompleteAnalysis } from '../lib/wasm-types';
import type { Card } from '../App';

export interface UseWasmAnalysisOptions {
  debounceMs?: number;
  autoAnalyze?: boolean;
}

export function useWasmAnalysis(
  deck: Card[],
  deckName: string,
  format: string,
  landCount: number,
  planTargetTurns: Record<string, number> = {},
  options: UseWasmAnalysisOptions = {}
) {
  const { debounceMs = 300, autoAnalyze = true } = options;
  
  const [analysis, setAnalysis] = useState<CompleteAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const performAnalysis = useCallback(async () => {
    if (deck.length === 0) {
      setAnalysis(null);
      setError(null);
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const totalCards = deck.reduce((sum, card) => sum + card.quantity, 0);
      
      // Build plan definitions from cards with plan requirements
      const planDefinitions: any[] = [];
      const planIds = [...new Set(deck.flatMap(c => c.plans || []))];
      
      planIds.forEach(planId => {
        const planCards = deck.filter(c => c.plans?.includes(planId));
        const cards = planCards
          .map(card => {
            const requirement = card.planRequirements?.[planId];
            const planEffectiveMV = requirement?.effectiveMV ?? card.effectiveMV ?? card.cmc;
            return {
              name: card.name,
              quantity: requirement?.copiesRequired ?? card.quantity, // Required copies
              copies_in_deck: card.quantity, // Total copies in deck
              effective_mv: planEffectiveMV, // Plan-specific MV for weight calculation
            };
          })
          .filter(c => c.quantity > 0);

        if (cards.length > 0) {
          planDefinitions.push({
            plan_id: planId,
            target_turn: planTargetTurns[planId] || 3,
            cards: cards,
          });
        }
      });

      const request: DeckAnalysisRequest = {
        cards: deck.map(card => {
          // Parse pips from mana cost (e.g., "{2}{U}{U}" -> { U: 2 })
          const pips: Record<string, number> = {};
          const manaCostMatch = card.manaCost.match(/\{([WUBRG])\}/g);
          if (manaCostMatch) {
            manaCostMatch.forEach(match => {
              const color = match.replace(/[{}]/g, '');
              pips[color] = (pips[color] || 0) + 1;
            });
          }

          return {
            name: card.name,
            quantity: card.quantity,
            effective_mv: card.effectiveMV ?? card.cmc,
            is_land: card.type.toLowerCase().includes('land'),
            pips: pips,
            ramp_strength: card.rampNet ?? 0.0,
            cantrip_value: card.cantrip ?? 0.0,
            plans: (card.plans || []).map(p => p.charAt(0)), // Convert to single chars
            plan_mv_overrides: card.planMVs || {},
          };
        }),
        land_count: landCount,
        deck_size: totalCards,
        ramp_multiplier: 1.0,
        on_play: true,
        plans: planDefinitions,
      };

      const result = await analyzeDeck(request);
      setAnalysis(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Analysis failed';
      setError(message);
      console.error('Analysis error:', err);
    } finally {
      setIsAnalyzing(false);
    }
  }, [deck, deckName, format, landCount, planTargetTurns]);

  // Auto-analyze with debounce
  useEffect(() => {
    if (!autoAnalyze) return;

    const timer = setTimeout(() => {
      performAnalysis();
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [performAnalysis, debounceMs, autoAnalyze]);

  return {
    analysis,
    isAnalyzing,
    error,
    reanalyze: performAnalysis,
  };
}

